import moment from 'moment'
import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { DetailListItem } from '@/common/ui/DetailListItem'
import { ONBOARDING_COPY } from '@/locales/en'
import { getAddressString } from '@/common/utils/utils'
import { formatMobileForDisplay } from '@/user/mobile/MobileUtils'
import { AccountConfirmationFormValues } from '../rules/accountConfirmationSchema'

export type AccountConfirmationFormProps = {
    progress?: number
    defaultValues?: AccountConfirmationFormValues
    loading?: boolean
    saving?: boolean
    disabled?: boolean
    onSubmit?: () => Promise<void>
}

export function AccountConfirmationForm({
    progress = 75,
    defaultValues,
    loading,
    saving,
    disabled,
    onSubmit,
}: AccountConfirmationFormProps) {
    const isDisabled = disabled ?? (loading || saving)

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit()
        }
    }

    const formatDateOfBirth = (dob: string | undefined): string => {
        if (!dob) return 'N/A'
        const parsed = moment(dob)
        return parsed.isValid() ? parsed.format('Do MMMM, YYYY') : 'N/A'
    }

    return (
        <Onboarding
            title={ONBOARDING_COPY.confirmation.title}
            isLoading={saving || loading}
            description={
                defaultValues?.adviser
                    ? ONBOARDING_COPY.confirmation.descriptionWithAdviser
                    : ONBOARDING_COPY.confirmation.description
            }
            cta={ONBOARDING_COPY.confirmation.confirmButton}
            onSubmit={handleSubmit}
            disabled={isDisabled}
            progress={progress}
        >
            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.salutation}
                value={defaultValues?.salutation || 'N/A'}
            />
            <div className="border-b border-border-divider" />

            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.firstName}
                value={defaultValues?.firstName || 'N/A'}
            />
            <div className="border-b border-border-divider" />

            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.middleName}
                value={defaultValues?.middleName || 'N/A'}
            />
            <div className="border-b border-border-divider" />

            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.lastName}
                value={defaultValues?.lastName || 'N/A'}
            />
            <div className="border-b border-border-divider" />

            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.dateOfBirth}
                value={formatDateOfBirth(defaultValues?.dateOfBirth)}
            />
            <div className="border-b border-border-divider" />

            <DetailListItem
                label={ONBOARDING_COPY.confirmation.form.mobileNumber}
                value={formatMobileForDisplay(defaultValues?.mobile ?? undefined) || 'N/A'}
            />
            <div className="border-b border-border-divider" />

            <div>
                <div className="font-medium text-black text-sm mb-1">
                    {ONBOARDING_COPY.confirmation.form.residentialAddress}
                </div>
                <DetailListItem
                    label=""
                    value={defaultValues?.residential ? getAddressString(defaultValues.residential) : 'N/A'}
                />
            </div>
            <div className="border-b border-border-divider" />

            {defaultValues?.postal?.streetNumber && (
                <>
                    <div>
                        <div className="font-medium text-black text-sm mb-1">
                            {ONBOARDING_COPY.confirmation.form.postalAddress}
                        </div>
                        <DetailListItem
                            label=""
                            value={defaultValues?.postal ? getAddressString(defaultValues.postal) : 'N/A'}
                        />
                    </div>
                    <div className="border-b border-border-divider" />
                </>
            )}

            {defaultValues?.adviser && (
                <>
                    <DetailListItem
                        label={ONBOARDING_COPY.confirmation.adviserLabel}
                        value={[ONBOARDING_COPY.confirmation.adviserDescription, defaultValues.adviser]}
                    />
                    <div className="border-b border-border-divider" />
                    <p className="text-content-secondary text-sm font-normal leading-5">
                        {ONBOARDING_COPY.confirmation.contactAdviserNote}
                    </p>
                </>
            )}
        </Onboarding>
    )
}
